import {
  makeContentServiceGetRequest,
  makeContentServicePostRequest,
  contentServiceDelete,
  contentServiceGet,
  contentServicePost,
} from 'infrastructure/contentService'
import {
  ContentServiceFlowResults,
  ContentServiceFlowStartExampleResponse,
  ContentServiceFlowStatus,
  ContentServiceFlowStatusResponse,
  ExternalUrlsAccumulatorJob,
  FlowResult,
  GDriveFileResponse,
  LessonMappingResultResponse,
  RequestError,
  PdfTreeTranslateDiscoveryJob,
  PdfTranslationResultJob,
  TreeCreateData,
  TreeCreateResponse,
  TreeOperationResultResponse,
  TreeOperationResultSuccess,
  TreeStructureResponse,
  UrlVerificationJob,
  UrlReplacementJob,
} from './types'
import { UpdateData } from '../tree/nodeUpdates/types'
import { OperationMetadata, OperationMetadataResponse } from './types'

/**
 * Gets the status of a job flow by its ID.
 *
 * @param flowId - The unique identifier of the job flow to check
 *
 * @returns Promise<Response> - Response containing flow status and details
 */
export async function getFlowStatus<T = ContentServiceFlowStatus>(
  flowId: string,
): Promise<T | RequestError> {
  const { result, error, status } =
    await contentServiceGet<ContentServiceFlowStatusResponse>({
      path: `content/v1/job-flow/${flowId}/status`,
    })

  if (error || !result) {
    return {
      requestError: true,
      errorText: `Flow status check failed with status ${status}: ${error}`,
    }
  }

  const totalJobs =
    result.data.dependant_jobs.total + result.data.worker_jobs.total
  const completedJobs =
    result.data.dependant_jobs.completed + result.data.worker_jobs.completed
  const failedJobs =
    result.data.dependant_jobs.failed +
    result.data.worker_jobs.failed +
    (result.errors ? result.errors.length : 0)

  return {
    ...result,
    meta: {
      ...result.meta,
      totalJobs,
      completedJobs,
      failedJobs,
      finishedJobs: completedJobs + failedJobs,
    },
  } as T
}

export async function deleteFlow(flowId: string) {
  return contentServiceDelete({
    path: `content/v1/job-flow/${flowId}/delete`,
  })
}

/**
 * Gets the results of a completed job flow by its ID.
 *
 * @param flowId - The unique identifier of the job flow to get results for
 *
 * @returns Promise<Response> - Response containing flow results and details
 *
 */
export async function getFlowResults<T = ContentServiceFlowResults>(
  flowId: string,
): Promise<T | RequestError> {
  const response = await makeContentServiceGetRequest(
    `content/v1/job-flow/${flowId}/results`,
  )

  if (!response.ok) {
    const errorText = await response.text()
    return {
      requestError: true,
      errorText: `Flow results retrieval failed with status ${response.status}: ${errorText}`,
    }
  }

  return response.json()
}

/**
 * Starts an example job flow by calling the flow start API.
 *
 * @param parameters - Parameters to pass to the flow
 * @returns Promise<string> - The flow ID of the started flow
 *
 * @throws Error if the API call fails or no flow ID is returned
 */
export async function startExampleJobFlow(
  parameters: Record<string, unknown> = {},
): Promise<string> {
  const response = await makeContentServicePostRequest(
    'content/v1/job-flow/test/start',
    parameters,
  )

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(
      `Flow start failed with status ${response.status}: ${errorText}`,
    )
  }

  const data: ContentServiceFlowStartExampleResponse = await response.json()

  if (!data.data?.flow_id) {
    throw new Error('No flow ID received from API')
  }

  return data.data.flow_id
}

export function isRequestError(response: object): response is RequestError {
  return (response as RequestError).requestError === true
}

/**
 * Checks if the error is an unauthorized (401) response
 */
export function isUnauthorizedResponse(
  error: RequestError | ContentServiceFlowStatus | undefined,
): boolean {
  if (!error || !isRequestError(error)) return false
  return error.errorText.toUpperCase().includes('401')
}

/**
 * Checks if the error is a not found (404) response
 */
export function isNotFoundResponse(
  error: RequestError | ContentServiceFlowStatus | undefined,
): boolean {
  if (!error || !isRequestError(error)) return false
  return error.errorText.toUpperCase().includes('404: NOT FOUND')
}

/**
 * Checks if the error is a permanent error (401 or 404)
 * Permanent errors should stop polling and disable the button
 */
export function isPermanentError(
  error: RequestError | ContentServiceFlowStatus | undefined,
): boolean {
  return isUnauthorizedResponse(error) || isNotFoundResponse(error)
}

export async function doTreeOperationsJob(updates: UpdateData[]) {
  const body = updates.map((update) => {
    if (update.type === 'leaf') {
      const { nodeId, ...restIds } = update.ids
      return { ...update, ids: { leafId: nodeId, ...restIds } }
    }
    return update
  })
  return contentServicePost<{ data: { flow_id: string } }>({
    path: '/content/v1/kiddom/tree/operations/job',
    body,
  })
}

export async function getWorkerFlowResult<TResult extends FlowResult>(
  flowId: string,
) {
  return await contentServiceGet<TResult>({
    path: `/content/v1/worker-flow/${flowId}/results`,
  })
}

export async function getJobMetadata(operations: UpdateData[]) {
  const { result } = await contentServicePost<OperationMetadataResponse>({
    path: '/content/v1/kiddom/tree/operations/metadata',
    body: operations,
  })
  return result
}

export function metadataCountItems(metadata: OperationMetadata) {
  return Object.entries(metadata).flatMap(([type, methods]) => {
    return Object.entries(methods).map(([method, count]) => {
      return { type, method, count }
    })
  })
}

export function successCountItems(results: TreeOperationResultSuccess[]) {
  return Object.entries(groupSuccess(results)).flatMap(([type, methods]) => {
    return Object.entries(methods).map(([method, count]) => {
      return { type, method, count }
    })
  })
}

function groupSuccess(results: TreeOperationResultSuccess[]) {
  return results.reduce(
    (acc, { type, method }) => {
      if (!acc[type]) acc[type] = {}
      if (!acc[type][method]) acc[type][method] = 0
      acc[type][method] += 1
      return acc
    },
    {} as Record<string, Record<string, number>>,
  )
}

export function isLessonMappingResponse(
  response: FlowResult,
): response is LessonMappingResultResponse {
  return response.meta.worker_type === 'scope_sequence_lesson_mappings'
}

export function isGDriveResponse(
  response: FlowResult,
): response is GDriveFileResponse {
  return response.meta.worker_type === 'gdrive_files'
}

export function isTreeOperationsResponse(
  response: FlowResult,
): response is TreeOperationResultResponse {
  return response.meta.worker_type === 'tree_operations_result'
}

export function isTreeStructureResponse(
  response: FlowResult,
): response is TreeStructureResponse {
  return response.meta.worker_type === 'scope_sequence_tree_structure'
}

export function isTreeCreateResponse(
  response: FlowResult,
): response is TreeCreateResponse {
  return response.meta.worker_type === 'scope_sequence_tree_create'
}

export function isExternalUrlsAccumulatorResponse(
  response: FlowResult,
): response is ExternalUrlsAccumulatorJob {
  return response.meta.worker_type === 'external_urls_result'
}

export function isUrlVerificationResponse(
  response: FlowResult,
): response is UrlVerificationJob {
  return response.meta.worker_type === 'url_verification_result'
}

export function isUrlReplacementResponse(
  response: FlowResult,
): response is UrlReplacementJob {
  return response.meta.worker_type === 'url_replacement_result'
}

export function isPdfTreeTranslateDiscoveryResponse(
  response: FlowResult,
): response is PdfTreeTranslateDiscoveryJob {
  return response.meta.worker_type === 'pdf_discovery_result'
}

export function isPdfTranslationResultResponse(
  response: FlowResult,
): response is PdfTranslationResultJob {
  return response.meta.worker_type === 'pdf_translation_result'
}

/**
 * Starts a URL verification job for a curriculum tree.
 *
 * @param treeId - The tree identifier
 * @param versionId - The version identifier
 * @returns Promise<string> - The flow ID of the started verification job
 *
 * @throws Error if the API call fails or no flow ID is returned
 */
export async function startUrlVerification({
  tree_id,
  version_id,
}: {
  tree_id: string
  version_id: string
}): Promise<string> {
  const { result, error } = await contentServicePost<{ flow_id: string }>({
    path: '/content/v1/url_verification',
    body: { tree_id, version_id },
  })

  if (error || !result?.flow_id) {
    throw new Error(
      error
        ? `URL verification start failed: ${error}`
        : 'No flow ID received from API',
    )
  }

  return result.flow_id
}

/**
 * Starts a PDF discovery job for a curriculum tree.
 *
 * @param tree_id - The tree identifier
 * @param version_id - The version identifier
 * @returns Promise<string> - The flow ID of the started discovery job
 *
 * @throws Error if the API call fails or no flow ID is returned
 */
export async function startPdfTreeTranslateDiscovery({
  tree_id,
  version_id,
}: {
  tree_id: string
  version_id: string
}): Promise<string> {
  const { result, error } = await contentServicePost<{
    data: { flow_id: string }
  }>({
    path: `/content/v1/pdf-tree-translate/${tree_id}:${version_id}/discover`,
  })

  if (error || !result?.data?.flow_id) {
    throw new Error(
      error
        ? `PDF discovery start failed: ${error}`
        : 'No flow ID received from API',
    )
  }

  return result.data.flow_id
}
